import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import cn from "classnames";
import FormConfirm from "components/form/form-confirm";
import DataGrid, { DataGridItem } from "components/data-grid";
import { injectIntl } from "react-intl";
import OptionalDate from "components/optional-date";
import styles from "./styles/data.css.js";
import withStyles from "components/with-styles";
import Chip from "components/chip";
import Divider from "@material-ui/core/Divider";
import Card from "@material-ui/core/Card";
import { ActionWithModal, DeleteActionWithModal } from "components/buttons";
import { eventStatusTypes } from "pages/event/utils";
import { deleteEventAction } from "../../../services/commonService";
import { mutation as stopEventMutation } from "./data-page";
import { useMutation } from "@apollo/react-hooks";
import { Restricted, RestrictedToAdmin, permissions, useAuthDataContext } from "components/auth";
import { ExternalLink } from "components/link";
import { useImperativeTracking } from "components/tracking";
import TextDisplay from "../../../components/display-data/text/text";

const EventData = ({ event, intl, classes, customerId }) => {
  const track = useImperativeTracking();
  const [success, setSuccess] = useState(false);
  const [show, setShow] = useState(false);
  const [message, setMessage] = useState(false);
  const handleCloseConfirm = useCallback(() => setShow(false), []);
  const hasRecordings = event && event.recordings && event.recordings.length > 0;
  const { hasAdminPrivileges, user } = useAuthDataContext();

  const [stopEvent] = useMutation(stopEventMutation);

  const now = moment();
  const isEventOnTime = moment(event.startDate).isBefore(now) && moment(event.endDate).isAfter(now);
  const isEventOnError = event.eventStatus && event.eventStatus === eventStatusTypes.ERROR;
  const isEventStarted =
    event.eventStatus &&
    (event.eventStatus === eventStatusTypes.STARTED || event.eventStatus === eventStatusTypes.STARTING);
  const isEventDeleted = event && event.eventStatus === eventStatusTypes.DELETED;

  return (
    <>
      <FormConfirm
        className={classes.confirm}
        autoHideDuration={2000}
        show={show}
        error={!success}
        success={success}
        onClose={handleCloseConfirm}
        hideCloseButton={false}
        label={message ? message : ""}
      />
      <DataGrid>
        <DataGridItem threeQuarter>
          <div className={classes.subtitle}>{event.type && event.type.description}</div>
          <div className={classes.text}>{event.title}</div>
          {event.description !== event.title && <div className={classes.text}>{event.description}</div>}
          <RestrictedToAdmin>
            <Chip
              className={cn(classes.chip__disabled, {
                [classes.chip__enabled]: event.isInternalTest,
              })}
              variant="outlined"
              label={intl.formatMessage({ id: `event.isInternalTest.in.${event.isInternalTest}` })}
            />
            <Chip
              className={cn(classes.chip__disabled, {
                [classes.chip__enabled]: event.visible,
              })}
              variant="outlined"
              label={intl.formatMessage({ id: `event.visible.in.${event.visible}` })}
            />
            <Chip
              className={cn(classes.chip__disabled, {
                [classes.chip__enabled]: event.billable,
              })}
              variant="outlined"
              label={intl.formatMessage({ id: `event.billable.in.${event.billable}` })}
            />
            <Chip
              className={cn(classes.chip__disabled, {
                [classes.chip__enabled]: event.localRec,
              })}
              variant="outlined"
              label={intl.formatMessage({ id: `event.local.rec.in.${!!event.localRec}` })}
            />
            <Chip
              className={cn(classes.chip__disabled, {
                [classes.chip__enabled]: event.activeQod,
              })}
              variant="outlined"
              label={intl.formatMessage({ id: `event.activeQod.in.${!!event.activeQod}` })}
            />
          </RestrictedToAdmin>
        </DataGridItem>
        <DataGridItem small>
          <DataGrid>
            <DataGridItem big className={classes.statusContainer}>
              {event.eventStatus ? (
                <Chip
                  className={classes.status}
                  variant="outlined"
                  label={intl.formatMessage({ id: `event.eventStatus.in.${event.eventStatus}` })}
                />
              ) : null}
              {event.eventStatus === eventStatusTypes.DELETED && (
                <div className={classes.deleteContainer}>
                  {!!event?.deletedBy && (
                    <TextDisplay className={classes.data} label={intl.formatMessage({ id: "event.deletedBy" })}>
                      {event.deletedBy}
                    </TextDisplay>
                  )}
                  {!!event?.deletedAt && (
                    <OptionalDate
                      label={intl.formatMessage({ id: "event.deletedAt" })}
                      value={event.deletedAt * 1000}
                    />
                  )}
                </div>
              )}
            </DataGridItem>
            <RestrictedToAdmin>
              <DataGridItem big>
                {(event.infoBert?.componentAnyAbnormal || event.infoBert?.elementAnyAbnormal) && (
                  <Chip
                    className={classes.degradated}
                    variant="outlined"
                    label={intl.formatMessage({ id: "event.degradated" })}
                  />
                )}
              </DataGridItem>
            </RestrictedToAdmin>
            <DataGridItem big>
              <div className={classes.buttonsContainer}>
                {((event.eventStatus && event.eventStatus === eventStatusTypes.STARTED) ||
                  (isEventOnTime && isEventOnError)) && (
                  <ActionWithModal
                    reloadOnSuccess={false}
                    showLoading={false}
                    actionButton
                    dialogTitle={intl.formatMessage({ id: "app.event.stopnow.modal.title" })}
                    renderDialogContent={() => intl.formatMessage({ id: "app.event.stopnow.modal.content" })}
                    icon="stop"
                    elementNameInResult="stopEvent"
                    mutation={stopEvent}
                    onSuccess={() => {
                      setMessage(intl.formatMessage({ id: "app.event.stopnow.success" }));
                      track(`user stopped event [${event.id}]`);
                      setSuccess(true);
                      setShow(true);
                    }}
                    onError={() => {
                      setMessage(intl.formatMessage({ id: "app.event.stopnow.error" }));
                      setSuccess(false);
                      setShow(true);
                    }}
                    mutationVariables={{ customerId, eventId: event.id }}
                  />
                )}
                <Restricted permissions={[permissions.event_manage]}>
                  {!isEventDeleted && isEventOnTime && isEventOnError && hasAdminPrivileges && (
                    <div>
                      <DeleteActionWithModal
                        disabledTooltipText={intl.formatMessage({ id: "app.event.delete.hasRecordings" })}
                        disabled={hasRecordings}
                        actionButton
                        urlOnSuccess="/event/list"
                        history={history}
                        mutation={deleteEventAction}
                        mutationVariables={{ eventId: event.id, username: user?.sub }}
                        elementNameInResult="deleteEvent"
                        elementToDelete={event ? event.title : ""}
                        event={event}
                      />
                    </div>
                  )}
                </Restricted>
              </div>
            </DataGridItem>
          </DataGrid>
        </DataGridItem>
        <RestrictedToAdmin>
          {isEventStarted ? (
            <DataGridItem threeQuarter>
              <ExternalLink href={event.bertSimpleUrl} className={classes.link}>
                {intl.formatMessage({ id: "event.bertSimpleUrl" })}
              </ExternalLink>
            </DataGridItem>
          ) : null}
        </RestrictedToAdmin>
        {isEventStarted && event.matchPointConfig && event.matchPointConfig.matchPointUrl && (
          <DataGridItem threeQuarter>
            <ExternalLink href={event.matchPointConfig.matchPointUrl} className={classes.link}>
              {intl.formatMessage({ id: "event.matchPointUrl" })}
            </ExternalLink>
          </DataGridItem>
        )}
      </DataGrid>

      <DataGrid className={classes.dates}>
        <Card className={classes.dataCard}>
          <OptionalDate
            label={intl.formatMessage({ id: "event.startDate" })}
            value={event.startDate}
            icon="event-available"
          />
        </Card>
        <Card className={classes.dataCard}>
          <OptionalDate
            className={classes.date}
            label={intl.formatMessage({ id: "event.endDate" })}
            value={event.endDate}
            icon="event-available"
          />
        </Card>
      </DataGrid>

      {event && event.keywords && event.keywords.length > 0 && (
        <>
          <Divider className={classes.divider} />

          <DataGridItem big>
            <div className={classes.keywordContainer}>
              {event.keywords.map(keyword => (
                <Chip className={classes.keyword} key={keyword} label={keyword} />
              ))}
            </div>
          </DataGridItem>
        </>
      )}
    </>
  );
};

EventData.propTypes = {
  /**
   * Data to represent
   **/
  event: PropTypes.object,
  /**
   * Id del customer
   **/
  customerId: PropTypes.string,
};

export default withStyles(styles)(injectIntl(EventData));
